import { useState, useRef, useEffect } from 'react';
import { BENCHMARK, benchmarkCloses, performancePoints, performanceSummary, weeklyPerformance, shiftDay } from './netWorth';
import { fmtBal, fmtDayShort } from './format';

// Two sections of the Net Worth tab, read-only like the rest of it:
//   You vs. the Market — our return against the S&P 500 over the same days
//   Week by Week       — what the investments were worth each week and how
//                        that week went, against the S&P 500
// netWorth.js does the arithmetic; this file only draws it.

const YOU_COLOR = '#4B0082', BENCH_COLOR = '#B8860B';
const UP = '#2e7d32', DOWN = '#c62828', FLAT = '#888';
const tone = n => (n > 0.00005 ? UP : n < -0.00005 ? DOWN : FLAT);
const fmtPct = n => `${n > 0.00005 ? '+' : n < -0.00005 ? '−' : ''}${Math.abs(n * 100).toFixed(1)}%`;
const fmtSignedBal = n => (Math.round(n) > 0 ? '+' : '') + fmtBal(n);
// "0.6 pts ahead" / "0.6 pts behind" / "even"
const fmtGap = n => (Math.abs(n) < 0.0005 ? 'even' : `${Math.abs(n * 100).toFixed(1)} pts ${n > 0 ? 'ahead' : 'behind'}`);
// The same gap for a narrow table column: "+1.0" ahead, "−0.1" behind, in points.
const fmtGapShort = n => (Math.abs(n) < 0.0005 ? '0.0' : `${n > 0 ? '+' : '−'}${Math.abs(n * 100).toFixed(1)}`);

const CH_H = 190, CH_PAD = { l: 44, r: 14, t: 14, b: 24 };
function niceStep(raw) {
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  return [1, 2, 2.5, 5, 10].map(m => m * pow).find(s => s >= raw);
}

// Both lines start at 0% on the same day. Ours is solid indigo; the S&P 500 is
// dashed gold, so the two are told apart by more than colour.
function VsChart({ line }) {
  const wrap = useRef(null);
  const [width, setWidth] = useState(660);
  useEffect(() => {
    const measure = () => { if (wrap.current?.clientWidth) setWidth(wrap.current.clientWidth); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const vals = line.flatMap(p => [p.you, p.bench]).filter(v => v !== null).map(v => v * 100);
  let lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  if (hi === lo) { lo -= 1; hi += 1; }
  const step = niceStep((hi - lo) / 3);
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);

  const plotW = width - CH_PAD.l - CH_PAD.r, plotH = CH_H - CH_PAD.t - CH_PAD.b;
  const x = i => CH_PAD.l + (line.length > 1 ? (i / (line.length - 1)) * plotW : plotW / 2);
  const y = v => CH_PAD.t + plotH - ((v * 100 - lo) / (hi - lo)) * plotH;
  const path = key => line.map((p, i) => (p[key] === null ? null : `${x(i)},${y(p[key])}`)).filter(Boolean).join(' ');
  const lastOf = key => { for (let i = line.length - 1; i >= 0; i--) if (line[i][key] !== null) return i; return -1; };
  const youEnd = lastOf('you'), benchEnd = lastOf('bench');

  return (
    <div className="fin-nw-chart" ref={wrap}>
      <svg width={width} height={CH_H} role="img"
        aria-label={`Return since ${fmtDayShort(line[0].day)}: ours and the ${BENCHMARK.label}, in percent`}>
        {ticks.map(v => (
          <g key={v}>
            <line x1={CH_PAD.l} x2={width - CH_PAD.r} y1={y(v / 100)} y2={y(v / 100)} stroke={v === 0 ? '#c4b89a' : '#ece6dc'} strokeWidth="1" />
            <text x={CH_PAD.l - 8} y={y(v / 100) + 3.5} textAnchor="end" fontSize="10" fill="#7a6a5a">{v > 0 ? '+' : ''}{v}%</text>
          </g>
        ))}
        <polyline className="fin-perf-bench" fill="none" stroke={BENCH_COLOR} strokeWidth="2" strokeDasharray="5 4" strokeLinejoin="round" strokeLinecap="round" points={path('bench')} />
        <polyline className="fin-perf-you" fill="none" stroke={YOU_COLOR} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" points={path('you')} />
        {benchEnd >= 0 && <circle cx={x(benchEnd)} cy={y(line[benchEnd].bench)} r="3.5" fill={BENCH_COLOR} stroke="#fff" strokeWidth="2" />}
        {youEnd >= 0 && <circle cx={x(youEnd)} cy={y(line[youEnd].you)} r="4" fill={YOU_COLOR} stroke="#fff" strokeWidth="2" />}
        <text x={CH_PAD.l} y={CH_H - 6} fontSize="10" fill="#7a6a5a">{fmtDayShort(line[0].day)}</text>
        <text x={width - CH_PAD.r} y={CH_H - 6} textAnchor="end" fontSize="10" fill="#7a6a5a">{fmtDayShort(line[line.length - 1].day)}</text>
      </svg>
    </div>
  );
}

// "Nov 2–6" / "Sep 28–Oct 2": the Friday is spelled out when the month turns.
function weekLabel(monday) {
  const friday = shiftDay(monday, 4);
  return `${fmtDayShort(monday)}–${friday.slice(5, 7) === monday.slice(5, 7) ? Number(friday.slice(8)) : fmtDayShort(friday)}`;
}

export default function PerformanceSection({ snaps, invTxns, maskById, holdings, manualInvestments = 0 }) {
  const points  = performancePoints(snaps, invTxns, maskById, benchmarkCloses(holdings));
  const summary = performanceSummary(points);
  const weeks   = weeklyPerformance(points);
  const thisWeek = weeks[0]?.week;

  return (
    <>
      <div className="fin-section">
        <h3 className="fin-section-title">You vs. the Market</h3>
        {!summary ? <p className="fin-empty">Not enough history yet to compare with the {BENCHMARK.label}.</p> : (
          <>
            <div className="fin-perf-summary">
              <span className="fin-perf-key"><i style={{ background: YOU_COLOR }} />You <strong style={{ color: tone(summary.you) }}>{fmtPct(summary.you)}</strong></span>
              <span className="fin-perf-key"><i className="fin-perf-dash" style={{ color: BENCH_COLOR }} />{BENCHMARK.label} <strong style={{ color: tone(summary.bench) }}>{fmtPct(summary.bench)}</strong></span>
              <span className="fin-perf-gap" style={{ color: tone(summary.diff) }}>{fmtGap(summary.diff)}</span>
            </div>
            <VsChart line={summary.line} />
            <p className="fin-nw-foot">
              {fmtDayShort(summary.from)} to {fmtDayShort(summary.to)}, in percent, so the amounts don’t matter: money added or taken out is set aside.
              Covers the Chase investment accounts 6181 · 8223 · 9454; the manual accounts have no daily history.
              The {BENCHMARK.label} is {BENCHMARK.ticker}’s price, without its dividends. These accounts also hold bonds and international funds, so an all-stock index is a tough yardstick.
            </p>
          </>
        )}
      </div>

      <div className="fin-section">
        <h3 className="fin-section-title">Week by Week</h3>
        {weeks.length === 0 ? <p className="fin-empty">No weekly history yet.</p> : (
          <div className="fin-chart-wrap">
            <table className="fin-nw-table fin-nw-table--weeks">
              <thead>
                <tr><th>Week</th><th className="num">Value</th><th className="num">Change</th><th className="num">You</th><th className="num">S&amp;P</th><th className="num" title="Points ahead of or behind the S&P 500">±</th></tr>
              </thead>
              <tbody>
                {weeks.map(w => (
                  <tr key={w.week}>
                    <td>
                      {weekLabel(w.week)}
                      {w.week === thisWeek && <div className="fin-nw-sub">this week so far</div>}
                    </td>
                    <td className="num">{fmtBal(w.value + manualInvestments)}</td>
                    <td className="num" style={{ color: tone(w.change) }}>
                      {w.from === w.to ? '—' : fmtSignedBal(w.change)}
                      {Math.abs(w.netIn) >= 0.5 && <div className="fin-nw-sub">{fmtSignedBal(w.netIn)} {w.netIn > 0 ? 'added' : 'taken out'}</div>}
                    </td>
                    <td className="num" style={w.you !== null ? { color: tone(w.you) } : undefined}>{w.you !== null ? fmtPct(w.you) : '—'}</td>
                    <td className="num">
                      {w.bench !== null ? fmtPct(w.bench) : '—'}
                      {w.through && <div className="fin-nw-sub">thru {fmtDayShort(w.through)}</div>}
                    </td>
                    <td className="num" style={w.diff !== null ? { color: Math.abs(w.diff) < 0.0005 ? FLAT : tone(w.diff) } : undefined}>
                      {w.diff !== null ? fmtGapShort(w.diff) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {weeks.length > 0 && (
          <p className="fin-nw-foot">
            Value is all investments at the week’s close{manualInvestments > 0 ? ', manual accounts at their current value' : ''}.
            You and S&amp;P are the week’s return on the Chase accounts; ± is how many points you were ahead (+) or behind (−).
            “thru” means that day’s {BENCHMARK.label} close is the latest one in, so S&amp;P and ± stop there while You runs to today.
          </p>
        )}
      </div>
    </>
  );
}
